import { Box, Typography, Divider, CircularProgress, Button, Tooltip } from "@mui/material";
import { PureLightThemeCaaS } from "src/theme/schemes/PureLightTheme";
import CheckIcon from "@mui/icons-material/Check";
import StarIcon from "@mui/icons-material/Star";
import { isPlatform } from "@ionic/react";
import { Capacitor } from "@capacitor/core";
import { useState } from "react";
import { isDevFrontendEnv } from "src/config/frontendUrls";

// Icon components
const IconBase = ({ size = 20, className = "", children, ...props }: any) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    {...props}
  >
    {children}
  </svg>
);

const Shield = (props: any) => (
  <IconBase {...props}>
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
  </IconBase>
);

const Crown = (props: any) => (
  <IconBase {...props}>
    <path d="m2 4 3 12h14l3-12-6 7-4-7-4 7-6-7zm3 16h14" />
  </IconBase>
);

interface SubsPlansProps {
  monthlyPlans: any[];
  handleOptionClick: (creditsAmount: number, subscription: string, optionKey: string, productId: string) => void;
  hoveredMonthlyPlan: number | null;
  setHoveredMonthlyPlan: (index: number | null) => void;
  processingOption: string | null;
  userSubscription?: string | null;
}

const CURRENT_PLAN_SUBSCRIPTIONS = ["free", "basic_monthly", "monthly", "annual_pro"];

const SubsPlans = ({ monthlyPlans, handleOptionClick, hoveredMonthlyPlan, setHoveredMonthlyPlan, processingOption, userSubscription }: SubsPlansProps) => {
  const paymentsDisabledInCurrentEnv = isDevFrontendEnv();
  // const paymentsDisabledInCurrentEnv = false; // TEMP: enable payments in dev 

  const paymentsDisabledTooltip = "Payments are disabled in dev. Use prod to complete purchases.";
  const [openedTooltipKey, setOpenedTooltipKey] = useState<string | null>(null);

  const isNativePlatform = Capacitor.isNativePlatform();
  const isHybridPlatform = isPlatform("hybrid");
  const isRunningNatively = isNativePlatform || isHybridPlatform;
  return (
    <Box sx={{ pt: 2.5 }}>
      {monthlyPlans.map((plan, index) => {
        const isCurrentPlan =
          !!userSubscription &&
          CURRENT_PLAN_SUBSCRIPTIONS.includes(userSubscription) &&
          plan.subscription === userSubscription;

        // Determine button text based on plan
        const getButtonText = () => {
          if (isCurrentPlan) return "CURRENT PLAN";
          if (plan.name === "FREE") return "GET STARTED";
          if (plan.name === "BASIC MONTHLY") return "SUBSCRIBE";
          if (plan.name === "ROTOGPT MONTHLY") return "SUBSCRIBE";
          if (plan.name === "SEASON PASS" && plan.isDisabled) return "COMING SOON";
          if (plan.name === "SEASON PASS") return "LOCK IN SEASON";
          if (plan.name === "ROTOGPT ANNUAL") return "GO PRO";
          return "SUBSCRIBE";
        };

        const planButtonBorderColor =
          plan.name === "SEASON PASS"
            ? `${PureLightThemeCaaS.colors.alpha.orange[100]}`
            : plan.name === "ROTOGPT ANNUAL"
              ? "#9333ea"
              : `${PureLightThemeCaaS.colors.alpha.white[70]}`;
        const planButtonBgColor =
          plan.name === "SEASON PASS"
            ? `${PureLightThemeCaaS.colors.alpha.orange[100]}`
            : plan.name === "ROTOGPT ANNUAL"
              ? "#9333ea"
              : "rgba(0, 0, 0, 0.05)";

        return (
          <Box
            key={index}
            onMouseEnter={() => setHoveredMonthlyPlan(index)}
            onMouseLeave={() => setHoveredMonthlyPlan(null)}
            sx={{
              position: "relative",
              border:
                plan.name === "ROTOGPT ANNUAL"
                  ? hoveredMonthlyPlan === index
                    ? "2px solid #9333ea"
                    : "2px solid #6B21A8"
                  : plan.name === "SEASON PASS"
                    ? hoveredMonthlyPlan === index
                      ? `2px solid ${PureLightThemeCaaS.colors.alpha.orange[100]}`
                      : `2px solid ${PureLightThemeCaaS.colors.alpha.orange[50]}`
                    : hoveredMonthlyPlan === index
                      ? `2px solid ${PureLightThemeCaaS.colors.alpha.orange[100]}`
                      : `2px solid ${PureLightThemeCaaS.colors.alpha.white[10]}`,
              borderRadius: 2,
              mb: 2,
              bgcolor: "#151921",
              transition: "border-color 0.2s ease",
              display: "flex",
              flexDirection: "column",
              height: "284px",

            }}
          >
            {(plan.isPremium || plan.isBestValue) && (
              <Box
                sx={{
                  position: "absolute",
                  top: -12,
                  left: "50%",
                  transform: "translateX(-50%)",
                  bgcolor: plan.name === "ROTOGPT ANNUAL" ? "#9333ea" : `${PureLightThemeCaaS.colors.alpha.orange[100]}`,
                  color: "#FFFFFF",
                  px: 2,
                  py: 0.5,
                  borderRadius: 3,
                  fontSize: "12px",
                  fontWeight: "bold",
                }}
              >
                BEST VALUE
              </Box>
            )}
            {plan.isMostPopular && (
              <Box
                sx={{
                  position: "absolute",
                  top: -12,
                  left: "50%",
                  transform: "translateX(-50%)",
                  bgcolor: `${PureLightThemeCaaS.colors.alpha.orange[100]}`,
                  color: "#FFFFFF",
                  px: 2,
                  py: 0.5,
                  borderRadius: 3,
                  fontSize: "12px",
                  fontWeight: "bold",
                }}
              >
                MOST POPULAR
              </Box>
            )}
            {/* {plan.adSupported && (
            <Box
              sx={{
                position: "absolute",
                top: -12,
                right: 16,
                bgcolor: "#999999",
                color: "#FFFFFF",
                px: 1.5,
                py: 0.5,
                borderRadius: 3,
                fontSize: "11px",
                fontWeight: "bold",
              }}
            >
              AD SUPPORTED
            </Box>
          )} */}

            <Box sx={{ p: 2, pt: plan.isPremium || plan.isBestValue || plan.isMostPopular ? 2.5 : 2.5, flex: 1, display: "flex", flexDirection: "column" }}>
              {/* Top row: Title + Credits (left) | Price (right) */}
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                {/* Left side: Title and Credits */}
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Typography
                      variant="h5"
                      sx={{
                        fontSize: "20px",
                        fontWeight: "bold",
                        color:
                          plan.name === "SEASON PASS"
                            ? `${PureLightThemeCaaS.colors.alpha.orange[100]}`
                            : plan.name === "ROTOGPT ANNUAL"
                              ? "#9333ea"
                              : `${PureLightThemeCaaS.colors.alpha.white[100]}`,
                        lineHeight: 1.2,
                      }}
                    >
                      {plan.name}
                    </Typography>
                    {plan.name === "SEASON PASS" && (
                      <Shield
                        size={16}
                        style={{
                          color: plan.isMostPopular ? `${PureLightThemeCaaS.colors.alpha.orange[100]}` : `${PureLightThemeCaaS.colors.alpha.white[100]}`,
                        }}
                      />
                    )}
                    {plan.name === "ROTOGPT ANNUAL" && (
                      <Crown
                        size={16}
                        style={{
                          color: plan.isBestValue ? "#9333ea" : `${PureLightThemeCaaS.colors.alpha.white[100]}`,
                        }}
                      />
                    )}
                  </Box>
                  {/* Credits pill/badge */}
                  <Box
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      px: 1,
                      py: 0,
                      borderRadius: 0.5,
                      fontSize: "10px",
                      fontWeight: "bold",
                      border: "1px solid",
                      bgcolor:
                        plan.name === "SEASON PASS"
                          ? "rgba(255, 152, 0, 0.1)"
                          : plan.name === "ROTOGPT ANNUAL"
                            ? "rgba(147, 51, 234, 0.1)"
                            : plan.name === "BASIC MONTHLY"
                              ? "rgba(74, 123, 198, 0.1)"
                            : plan.name === "ROTOGPT MONTHLY"
                              ? "rgba(34, 211, 238, 0.1)"
                              : "transparent",
                      borderColor:
                        plan.name === "SEASON PASS"
                          ? `${PureLightThemeCaaS.colors.alpha.orange[100]}`
                          : plan.name === "ROTOGPT ANNUAL"
                            ? "#9333ea"
                            : plan.name === "BASIC MONTHLY"
                              ? "#4a7bc6"
                              : plan.name === "ROTOGPT MONTHLY"
                              ? "#22D3EE"
                              : `${PureLightThemeCaaS.colors.alpha.white[30]}`,
                      color:
                        plan.name === "SEASON PASS"
                          ? `${PureLightThemeCaaS.colors.alpha.orange[100]}`
                          : plan.name === "ROTOGPT ANNUAL"
                            ? "#9333ea"
                            : plan.name === "BASIC MONTHLY"
                              ? "#4a7bc6"
                            : plan.name === "ROTOGPT MONTHLY"
                              ? "#22D3EE"
                              : `${PureLightThemeCaaS.colors.alpha.white[70]}`,
                      width: "fit-content",
                    }}
                  >
                    {plan.credits}
                  </Box>
                </Box>

                {/* Right side: Price */}
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 0.3 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontSize: "24px",
                      fontWeight: "bold",
                      color: plan.isDisabled ? "rgba(255, 255, 255, 0.5)" : `${PureLightThemeCaaS.colors.alpha.white[100]}`,
                      lineHeight: 1,
                    }}
                  >
                    {plan.price.includes(" / ") ? plan.price.split(" / ")[0] : plan.price}
                  </Typography>
                  {plan.price.includes(" / ") && (
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: "11px",
                        color: plan.isDisabled ? "rgba(255, 255, 255, 0.5)" : `${PureLightThemeCaaS.colors.alpha.white[100]}`,
                        lineHeight: 1,
                      }}
                    >
                      {plan.price.split(" / ")[1]}
                    </Typography>
                  )}
                </Box>
              </Box>

              {/* Separator line */}
              <Divider
                sx={{
                  bgcolor: "rgba(255, 255, 255, 0.2)",
                  my: 1,
                }}
              />

              {/* Features list */}
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, flex: 1 }}>
                {plan.features.map((feature, featureIndex) => (
                  <Box key={featureIndex} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {feature.icon === "check" ? (
                      <CheckIcon
                        sx={{
                          color:
                            plan.name === "SEASON PASS"
                              ? `${PureLightThemeCaaS.colors.alpha.orange[100]}`
                              : plan.name === "ROTOGPT ANNUAL"
                                ? "#9333ea"
                                : `${PureLightThemeCaaS.colors.alpha.white[100]}`,
                          fontSize: "20px",
                          flexShrink: 0,
                        }}
                      />
                    ) : feature.icon === "cross" ? (
                      <Box
                        sx={{
                          color: "rgba(255, 255, 255, 0.5)",
                          fontSize: "18px",
                          flexShrink: 0,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: "20px",
                          height: "20px",
                          lineHeight: 1,
                        }}
                      >
                        ✕
                      </Box>
                    ) : (
                      <StarIcon
                        sx={{
                          color: `${PureLightThemeCaaS.colors.alpha.orange[100]}`,
                          fontSize: "20px",
                          flexShrink: 0,
                        }}
                      />
                    )}
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: "14px",
                        color: feature.icon === "cross" ? "rgba(255, 255, 255, 0.5)" : `${PureLightThemeCaaS.colors.alpha.white[100]}`,
                        lineHeight: 1.5,
                        // textDecoration: feature.icon === "cross" ? "line-through" : "none",
                      }}
                    >
                      {feature.text}
                    </Typography>
                  </Box>
                ))}
              </Box>

              {/* Separator line */}
              {/* <Divider
                sx={{
                  bgcolor: "#E0E0E0",
                  my: 1,
                }}
              /> */}
              {/* Auto-renewal info */}
              {plan.autoRenews && (
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: "11px",
                    color: `${PureLightThemeCaaS.colors.alpha.white[70]}`,
                    mt: 1,
                    ml: 1,
                    textAlign: "left",
                  }}
                >
                  {plan.autoRenews}
                </Typography>
              )}

              {/* Button */}
              <Box sx={{ display: "flex", justifyContent: "center", mt: 1 }}>
                <Tooltip
                  title={paymentsDisabledInCurrentEnv && !plan.isFree ? paymentsDisabledTooltip : ""}
                  placement="top"
                  arrow
                  open={openedTooltipKey === `plan-${index}`}
                  disableHoverListener
                  disableFocusListener
                  disableTouchListener
                  componentsProps={{
                    tooltip: {
                      sx: {
                        textAlign: "center",
                        width: "90%",
                        margin: "0 auto",
                      },
                    },
                  }}
                >
                  <span
                    onClick={() => {
                      if (!paymentsDisabledInCurrentEnv || plan.isFree) return;
                      setOpenedTooltipKey(`plan-${index}`);
                      window.setTimeout(() => setOpenedTooltipKey((current) => (current === `plan-${index}` ? null : current)), 2500);
                    }}
                    onTouchStart={() => {
                      if (!paymentsDisabledInCurrentEnv || plan.isFree) return;
                      setOpenedTooltipKey(`plan-${index}`);
                      window.setTimeout(() => setOpenedTooltipKey((current) => (current === `plan-${index}` ? null : current)), 2500);
                    }}
                  >
                    <Button
                      variant="outlined"
                      onClick={() => {
                        handleOptionClick(plan.creditsAmount, plan.subscription, `plan-${index}`, plan.productId);
                      }}
                      disabled={!!processingOption || plan.isDisabled || (paymentsDisabledInCurrentEnv && !plan.isFree) || isCurrentPlan}
                      sx={{
                        borderColor: planButtonBorderColor,
                        color: `${PureLightThemeCaaS.colors.alpha.white[100]}`,
                        bgcolor: planButtonBgColor,
                        borderRadius: 1,
                        px: 1,
                        textTransform: "none",
                        width: "140px",
                        "&:hover": {
                          borderColor: planButtonBorderColor,
                          bgcolor: planButtonBgColor,
                        },
                        "&.Mui-disabled": isCurrentPlan
                          ? {
                            opacity: 1,
                            borderColor: planButtonBorderColor,
                            color: `${PureLightThemeCaaS.colors.alpha.white[100]}`,
                            bgcolor: planButtonBgColor,
                          }
                          : {
                            borderColor: `${PureLightThemeCaaS.colors.alpha.black[30]}`,
                            color: `${PureLightThemeCaaS.colors.alpha.white[30]}`,
                            bgcolor: plan.name === "SEASON PASS" && plan.isDisabled ? "rgba(128, 128, 128, 0.3)" : "transparent",
                          },
                      }}
                    >
                      {processingOption === `plan-${index}` ? <CircularProgress size={25} sx={{ color: `${PureLightThemeCaaS.colors.alpha.orange[100]}` }} /> : getButtonText()}
                    </Button>
                  </span>
                </Tooltip>
              </Box>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

export default SubsPlans;
